import fetchApi from '../util/fetchApi';
import { extendIdleDeadline, getCurrentSession } from './session';

/**
 * Reports user activity to the server, which is the only thing that pushes back a session's inactivity
 * sign-out (`security/session/activity`). Token refreshes and background polling deliberately do not
 * count, so an unattended tab still idles out.
 *
 * Only real input counts — pointer, keyboard, wheel, touch — including work that never reaches the API,
 * such as filling in a long form. Reports are throttled: the server only needs to hear about activity
 * well within its idle timeout, not on every keystroke.
 */

export type SessionActivityResponse = {
  idleExpiresAt: string;
  idleExpiresInSeconds: number;
};

/** At most one report per this interval, per tab. */
export const ACTIVITY_THROTTLE_MS = 60_000;

const ACTIVITY_EVENTS = ['pointerdown', 'mousemove', 'keydown', 'wheel', 'touchstart'] as const;

let lastReportedAt = -Infinity;

/**
 * Tells the server the user is active and moves this tab's idle check to the new deadline — unless this
 * tab already did within the throttle interval, or there is no session. Failures are ignored: a missed
 * report only brings the idle deadline closer, and a dead session is detected by the regular refresh.
 */
export async function recordActivity(): Promise<void> {
  if (!getCurrentSession()) return;
  const now = Date.now();
  if (now - lastReportedAt < ACTIVITY_THROTTLE_MS) return;
  lastReportedAt = now;

  try {
    const fetchResponse: Response = await fetchApi.api.post('security/session/activity', { json: false });
    if (!fetchResponse.ok) return;
    const body = await fetchResponse.json();
    extendIdleDeadline((body.data as SessionActivityResponse).idleExpiresInSeconds);
  } catch {
    // Offline or similar: try again on the next input after the throttle interval.
  }
}

/** Starts reporting user input in this tab. Returns a function that stops it. */
export function startActivityTracking(): () => void {
  const handler = () => {
    recordActivity();
  };
  ACTIVITY_EVENTS.forEach(type => window.addEventListener(type, handler, { capture: true, passive: true }));
  return () => ACTIVITY_EVENTS.forEach(type => window.removeEventListener(type, handler, { capture: true }));
}

/** Test hook: forget all module state. */
export function __resetActivityForTests() {
  lastReportedAt = -Infinity;
}
